import { useState, useEffect } from 'react';
import { adminApi } from '../../../utils/api';
import { Spinner, Pagination } from '../shared';

const fmtTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const ACTIVITY_ICONS = {
    session: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
    ),
    upload: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
    ),
    share: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
    ),
};

export default function UserActivityTab({ userId }) {
    const [activities, setActivities] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 50;

    useEffect(() => {
        loadActivities();
    }, [userId, typeFilter, offset]);

    const loadActivities = async () => {
        setLoading(true);
        try {
            const result = await adminApi.getUserActivity(userId, {
                limit,
                offset,
                type: typeFilter || undefined,
            });
            setActivities(result.activities || []);
            setTotal(result.total || 0);
        } catch (err) {
            console.error('Failed to load activities:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading && activities.length === 0) {
        return <Spinner text="Loading activities..." center />;
    }

    return (
        <div className="h-full flex flex-col">
            <div className="shrink-0 mb-4">
                <select
                    value={typeFilter}
                    onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setOffset(0);
                    }}
                    className="h-9 rounded-md border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                >
                    <option value="">All Activities</option>
                    <option value="session">Sessions</option>
                    <option value="upload">Uploads</option>
                    <option value="share">Shares</option>
                </select>
            </div>

            <div className="flex-1 overflow-y-auto">
                {activities.length > 0 ? (
                    <div className="space-y-3">
                        {activities.map((activity) => (
                            <div key={activity.id} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0">
                                <div className="shrink-0 w-12 text-right text-xs text-gray-400 pt-1">
                                    {fmtTime(activity.timestamp)}
                                </div>
                                <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                    {ACTIVITY_ICONS[activity.type] || ACTIVITY_ICONS.session}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 truncate">{activity.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 text-center py-12">No activities found</p>
                )}
            </div>

            {total > limit && (
                <div className="shrink-0 mt-4">
                    <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
                </div>
            )}
        </div>
    );
}
